import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";

/** Matches `pro-dashboard-redesign.html` `.input` / `.textarea` tokens. */
const proControlClassName =
  "bg-pro-elevated border-[1.5px] border-pro-border rounded-[8px] px-3.5 text-[16px] text-pro-text placeholder:text-pro-text-faint outline-none transition-[border-color,box-shadow] duration-150 focus:border-lime focus:shadow-[0_0_0_2px_var(--lime-ring)] disabled:opacity-50 disabled:cursor-not-allowed";

const proControlErrorClassName =
  "border-danger focus:border-danger focus:shadow-[0_0_0_2px_color-mix(in_srgb,var(--danger)_20%,transparent)]";

const proInputVariants = cva(cn("input w-full", proControlClassName), {
  variants: {
    size: {
      default: "h-10",
      sm: "h-[34px]",
    },
  },
  defaultVariants: { size: "default" },
});

interface ProInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof proInputVariants> {
  error?: boolean;
}

const ProInput = forwardRef<HTMLInputElement, ProInputProps>(
  ({ className, size, error, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={cn(proInputVariants({ size }), error && proControlErrorClassName, className)}
        {...props}
      />
    );
  },
);

ProInput.displayName = "ProInput";

const proTextareaClassName = cn(
  "textarea w-full min-h-[80px] resize-none py-2.5 leading-normal",
  proControlClassName,
);

interface ProTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

const ProTextarea = forwardRef<HTMLTextAreaElement, ProTextareaProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(proTextareaClassName, error && proControlErrorClassName, className)}
        {...props}
      />
    );
  },
);

ProTextarea.displayName = "ProTextarea";

export {
  ProInput,
  type ProInputProps,
  ProTextarea,
  type ProTextareaProps,
  proControlClassName,
  proControlErrorClassName,
  proInputVariants,
};
